import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Button, Empty, Input, Segmented, Select, Space, Spin, Tag,
  Tooltip, Typography, Upload, message as antdMessage, notification,
} from "antd";
import {
  CloseOutlined, InfoCircleOutlined, LinkOutlined, PaperClipOutlined, ReloadOutlined, SearchOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { MdMailOutline } from "react-icons/md";
import { BsChatDots } from "react-icons/bs";

import * as api from "api/communication";
import * as chatbotApi from "api/chatbot";
import ChatComposer, { WhatsAppAttachmentMenu } from "components/Communication/ChatComposer";
import ChatMessage, { normalizeChatMessage, replyPreview } from "components/Communication/ChatMessage";
import ChatSessionActions from "components/Communication/ChatSessionActions";
import ConversationList from "components/Communication/ConversationList";
import EnvelopeMessage from "components/Communication/EnvelopeMessage";
import IdentityBadges from "components/Communication/IdentityBadges";
import LinkAccountModal from "components/Communication/LinkAccountModal";
import { Divider } from "components/Communication/MessageBubble";
import {
  CHANNELS, emptyChannelSources, metaFor, themeFor,
} from "components/Communication/constants";
import {
  withDateSeparators, withSubjectDividers,
} from "components/Communication/dateSeparators";
import SendTemplateModal from "components/WhatsApp/SendTemplateModal";
import useCommunicationSocket from "realtime/useCommunicationSocket";
import { refreshUnreadSummary, useUnreadSummary } from "realtime/unreadSummary";
import useUrlFilters from "hooks/useUrlFilters";

const { Text, Title } = Typography;

const POLL_MS = 15000;
// Website chat is live; this only runs while the websocket is down.
const CHAT_POLL_MS = 5000;
const TYPING_CLEAR_MS = 4000;
const TYPING_SEND_EVERY_MS = 3000;

// The inbox view lives in the URL so it can be shared as a link: channel,
// product, number/mailbox, search and the open conversation (`c`).
const URL_FILTERS = {
  channel: { type: "string", default: CHANNELS.WHATSAPP },
  product: { type: "string" },
  source: { type: "string" },
  search: { type: "string" },
  c: { type: "string" },
};
// Mirrors ATTACHMENT_TYPES / MAX_ATTACHMENT_BYTES in chatbot/widget_views.py.
const CHAT_ATTACH_ACCEPT =
  ".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.mp3,.mp4,.mov";
const CHAT_MAX_FILE_BYTES = 10 * 1024 * 1024;

// Quick messages per chatbot, fetched once per page load (they change rarely;
// a reload picks up edits made in the chatbot settings).
const quickMessageCache = new Map();
const loadQuickMessages = (chatbotId) => {
  if (!quickMessageCache.has(chatbotId)) {
    quickMessageCache.set(
      chatbotId,
      chatbotApi
        .loadChatbot(chatbotId)
        .then((response) => response.data?.quick_messages || [])
        .catch((error) => {
          quickMessageCache.delete(chatbotId);
          throw error;
        })
    );
  }
  return quickMessageCache.get(chatbotId);
};

const CHANNEL_ICON = {
  [CHANNELS.WHATSAPP]: <FaWhatsapp />,
  [CHANNELS.EMAIL]: <MdMailOutline />,
  [CHANNELS.CHAT]: <BsChatDots />,
};

const channelOptions = (unread) =>
  Object.values(CHANNELS).map((value) => ({
    value,
    label: (
      <Space size={4}>
        {CHANNEL_ICON[value]} {metaFor(value).label}
        {unread[value] ? (
          <Badge count={unread[value]} size="small" overflowCount={99} />
        ) : null}
      </Space>
    ),
  }));

const isChatRef = (ref) => String(ref || "").startsWith("ch:");

/** Chat rows arrive in the contract shape; the thread renders the envelope's. */
const normalizeRows = (ref, rows) =>
  isChatRef(ref) ? rows.map(normalizeChatMessage) : rows;

/** Fields a `chat.session` push may update on a conversation row. */
const SESSION_ROW_FIELDS = [
  "status", "assigned_to", "assigned_to_name", "ticket_id", "ticket_number",
  "unread_count", "last_message_preview", "last_message_at",
];

const chatTitle = (conversation) =>
  conversation.counterparty?.display_name
  || conversation.visitor?.name
  || conversation.visitor?.email
  || "Website visitor";

const chatSubtitle = (conversation) => {
  const visitor = conversation.visitor || {};
  const parts = [
    visitor.email || conversation.counterparty?.address,
    visitor.phone,
    visitor.page_url ? `on ${visitor.page_url}` : null,
    conversation.source?.label || conversation.source?.name
      ? `via ${conversation.source.label || conversation.source.name}`
      : null,
  ];
  const seen = new Set();
  return parts
    .filter((part) => part && !seen.has(part) && seen.add(part))
    .join(" · ");
};

/**
 * Applies staff-socket pushes to the open thread and the conversation list.
 * State setters are stable; the values that change per render are read
 * through refs so the socket handler never goes stale.
 */
const useSocketHandler = ({
  activeRef,
  conversations,
  setMessages,
  setConversations,
  setVisitorTyping,
  typingTimerRef,
  fetchConversations,
}) => {
  const activeRefRef = useRef(activeRef);
  activeRefRef.current = activeRef;
  // setState updaters run lazily, so "is this row loaded?" is read from here.
  const rowsRef = useRef(conversations);
  rowsRef.current = conversations;
  const fetchRef = useRef(fetchConversations);
  fetchRef.current = fetchConversations;
  const refetchTimerRef = useRef(null);

  // Several events for a brand-new chat arrive together; refetch once.
  const refetchSoon = useCallback(() => {
    clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => fetchRef.current(true), 400);
  }, []);

  useEffect(() => () => clearTimeout(refetchTimerRef.current), []);

  return useCallback(
    (event) => {
      const ref = event?.ref;
      if (!ref || !isChatRef(ref)) return;
      const isActive = ref === activeRefRef.current;
      const known = rowsRef.current.some((item) => item.id === ref);

      if (event.type === "chat.message" && event.message) {
        refreshUnreadSummary();
        // `internal` rides on the event (staff-only notes), not the message.
        const row = normalizeChatMessage(
          event.internal ? { ...event.message, internal: true } : event.message
        );
        const inbound = row.sender_type === "visitor";

        if (isActive) {
          setMessages((previous) =>
            previous.some((item) => item.id === row.id) ? previous : [...previous, row]
          );
          if (inbound) {
            clearTimeout(typingTimerRef.current);
            setVisitorTyping(false);
            api.markRead(ref).then(refreshUnreadSummary).catch(() => {});
          }
        }

        if (!known) {
          refetchSoon();
          return;
        }
        setConversations((previous) => {
          const index = previous.findIndex((item) => item.id === ref);
          if (index < 0) return previous;
          const next = previous.map((item) => {
            if (item.id !== ref) return item;
            const bump = inbound && !isActive;
            return {
              ...item,
              last_message_preview: row.body || item.last_message_preview,
              last_message_at: row.sent_at || item.last_message_at,
              unread_count: bump ? (item.unread_count || 0) + 1 : item.unread_count,
              is_read: bump ? false : item.is_read,
            };
          });
          // Most recent activity first, like the server orders it.
          return [next[index], ...next.slice(0, index), ...next.slice(index + 1)];
        });
        return;
      }

      if (event.type === "chat.session" && event.session) {
        const patch = {};
        SESSION_ROW_FIELDS.forEach((key) => {
          if (event.session[key] !== undefined) patch[key] = event.session[key];
        });
        if (isActive) {
          patch.unread_count = 0;
          patch.is_read = true;
        }
        if (!known) {
          refetchSoon();
          return;
        }
        setConversations((previous) =>
          previous.map((item) => (item.id === ref ? { ...item, ...patch } : item))
        );
        return;
      }

      if (event.type === "chat.typing" && isActive && event.sender_type === "visitor") {
        setVisitorTyping(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setVisitorTyping(false), TYPING_CLEAR_MS);
      }
    },
    [setMessages, setConversations, setVisitorTyping, typingTimerRef, refetchSoon]
  );
};

/**
 * One inbox for every channel.
 *
 * Filters run product -> number/mailbox, because that is how the CRM already
 * models a channel: `activitytemplate.WhatsappConfiguration` and
 * `EmailConfiguration` are per-product rows pointing at a number or a mailbox.
 * Product is optional throughout — a number with no configuration row belongs
 * to no product and would otherwise be unreachable.
 */
const CommunicationBox = () => {
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);

  const channel = filters.channel;
  const productId = filters.product || undefined;
  const sourceId = filters.source || undefined;
  const activeRef = filters.c || null;

  const [products, setProducts] = useState([]);
  const [channels, setChannels] = useState(emptyChannelSources);
  // What is typed stays local; the debounced value is the one in the URL.
  const debouncedSearch = filters.search || "";
  const [search, setSearch] = useState(debouncedSearch);

  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [linkAccountOpen, setLinkAccountOpen] = useState(false);
  // Website chat: the message the next reply quotes.
  const [replyTo, setReplyTo] = useState(null);
  // Website chat: saved replies of the chatbot the open conversation came in on.
  const [quickMessages, setQuickMessages] = useState([]);

  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [visitorTyping, setVisitorTyping] = useState(false);

  const endRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const theme = themeFor(channel);
  const meta = metaFor(channel);
  const isEmail = channel === CHANNELS.EMAIL;
  const isWhatsApp = channel === CHANNELS.WHATSAPP;
  const isChat = channel === CHANNELS.CHAT;
  const unread = useUnreadSummary();

  const conversation = useMemo(
    () => conversations.find((row) => row.id === activeRef) || null,
    [conversations, activeRef]
  );

  const chatbotId = isChat ? conversation?.source?.id : null;

  useEffect(() => {
    if (!chatbotId) {
      setQuickMessages([]);
      return undefined;
    }
    let live = true;
    loadQuickMessages(chatbotId)
      .then((items) => live && setQuickMessages(items))
      .catch(() => live && setQuickMessages([]));
    return () => {
      live = false;
    };
  }, [chatbotId]);

  const updateParams = setFilters;

  // -- lookups --------------------------------------------------------

  useEffect(() => {
    api
      .loadProducts()
      .then((response) => setProducts(api.rowsOf(response)))
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    api
      .loadChannels({ product: productId })
      .then((response) =>
        setChannels({ ...emptyChannelSources(), ...(response.data || {}) })
      )
      .catch(() => setChannels(emptyChannelSources()));
  }, [productId]);

  const sourceOptions = useMemo(
    () =>
      (channels[channel] || []).map((row) => ({
        value: row.id,
        label: row.unread_conversations
          ? `${row.label || row.name} (${row.unread_conversations})`
          : row.label || row.name,
      })),
    [channels, channel]
  );

  // A source that belongs to another product must not linger after the
  // product filter changes.
  useEffect(() => {
    if (sourceId && !sourceOptions.some((option) => option.value === sourceId)) {
      updateParams({ source: null, c: null });
    }
  }, [sourceOptions, sourceId, updateParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() !== debouncedSearch) updateParams({ search: search.trim() });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch, updateParams]);

  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => {
    setSearch((typed) => (typed.trim() === debouncedSearch ? typed : debouncedSearch));
  }, [debouncedSearch]);

  // -- conversations --------------------------------------------------

  const fetchConversations = useCallback(
    async (silent = false) => {
      if (!silent) setListLoading(true);
      try {
        const response = await api.loadConversations({
          channel,
          product: productId,
          // business_number / mailbox / chatbot, depending on the channel.
          [metaFor(channel).sourceParam]: sourceId,
          search: debouncedSearch,
        });
        setConversations(api.rowsOf(response));
        // The list just changed under us, so the tab badges may have too.
        refreshUnreadSummary();
      } catch (error) {
        if (!silent) antdMessage.error("Could not load conversations");
      }
      if (!silent) setListLoading(false);
    },
    [channel, productId, sourceId, debouncedSearch]
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // -- thread ---------------------------------------------------------

  const fetchMessages = useCallback(
    async (ref, silent = false) => {
      if (!ref) return;
      if (!silent) setThreadLoading(true);
      try {
        const response = await api.loadMessages(ref);
        setMessages(normalizeRows(ref, api.rowsOf(response)));
      } catch (error) {
        if (!silent) antdMessage.error("Could not load this conversation");
      }
      if (!silent) setThreadLoading(false);
    },
    []
  );

  const fetchIdentity = useCallback(async (ref) => {
    if (!ref) return;
    setIdentityLoading(true);
    try {
      const response = await api.loadIdentity(ref);
      setIdentity(response.data);
    } catch (error) {
      setIdentity(null);
    }
    setIdentityLoading(false);
  }, []);

  // The conversation whose thread is loaded (or loading), so a URL change —
  // a deep link, or a click in the notification bell — knows whether it
  // still has to open one.
  const loadedRefRef = useRef(null);

  const openConversation = useCallback(
    async (row) => {
      loadedRefRef.current = row.id;
      updateParams({ c: row.id });
      setMessages([]);
      setIdentity(null);
      setText("");
      setPendingFiles([]);
      setReplyTo(null);
      setSubject("");
      setVisitorTyping(false);
      await Promise.all([fetchMessages(row.id), fetchIdentity(row.id)]);
      try {
        await api.markRead(row.id);
        refreshUnreadSummary();
        setConversations((previous) =>
          previous.map((item) =>
            item.id === row.id ? { ...item, is_read: true, unread_count: 0 } : item
          )
        );
      } catch (error) {
        /* a failed read receipt is not worth interrupting anyone over */
      }
    },
    [updateParams, fetchMessages, fetchIdentity]
  );

  // Open the thread the URL names when it was not opened from the list: a deep
  // link, or a notification clicked while another conversation was open.
  useEffect(() => {
    if (!activeRef) {
      loadedRefRef.current = null;
      return;
    }
    if (loadedRefRef.current === activeRef) return;
    loadedRefRef.current = activeRef;
    setMessages([]);
    setIdentity(null);
    setText("");
    setPendingFiles([]);
    setReplyTo(null);
    setVisitorTyping(false);
    fetchMessages(activeRef);
    fetchIdentity(activeRef);
    api
      .markRead(activeRef)
      .then(() => {
        refreshUnreadSummary();
        setConversations((previous) =>
          previous.map((item) =>
            item.id === activeRef ? { ...item, is_read: true, unread_count: 0 } : item
          )
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRef]);

  useEffect(() => {
    const timer = setTimeout(
      () => endRef.current?.scrollIntoView({ behavior: "smooth" }),
      100
    );
    return () => clearTimeout(timer);
  }, [messages]);

  // -- polling --------------------------------------------------------

  const handleSocketEvent = useSocketHandler({
    activeRef,
    conversations,
    setMessages,
    setConversations,
    setVisitorTyping,
    typingTimerRef,
    fetchConversations,
  });
  const { connected: socketConnected, send: socketSend } = useCommunicationSocket(
    handleSocketEvent,
    { enabled: isChat }
  );

  // Anything pushed while the socket was down is lost; catch up on (re)connect.
  useEffect(() => {
    if (!isChat || !socketConnected) return;
    fetchConversations(true);
    if (activeRef) fetchMessages(activeRef, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChat, socketConnected]);

  // Website chat: the socket pushes everything; poll only while it is down.
  // The list is polled even with no thread open, so new chats show up.
  useEffect(() => {
    if (!isChat) return undefined;
    clearInterval(pollRef.current);
    if (socketConnected) return undefined;
    pollRef.current = setInterval(() => {
      if (document.hidden) return;
      if (activeRef) fetchMessages(activeRef, true);
      fetchConversations(true);
    }, CHAT_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [isChat, socketConnected, activeRef, fetchMessages, fetchConversations]);

  useEffect(() => {
    if (isChat) return undefined;
    clearInterval(pollRef.current);
    if (!activeRef) return undefined;
    pollRef.current = setInterval(() => {
      // Nothing is arriving that the reader can see in a hidden tab, and the
      // old page polled from background tabs indefinitely.
      if (document.hidden) return;
      fetchMessages(activeRef, true);
      fetchConversations(true);
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [isChat, activeRef, fetchMessages, fetchConversations]);

  useEffect(() => () => clearTimeout(typingTimerRef.current), []);

  const patchConversation = useCallback((ref, patch) => {
    setConversations((previous) =>
      previous.map((item) => (item.id === ref ? { ...item, ...patch } : item))
    );
  }, []);

  const onComposerChange = useCallback(
    (value) => {
      setText(value);
      if (!isChat || !activeRef || !value) return;
      const now = Date.now();
      if (now - lastTypingSentRef.current > TYPING_SEND_EVERY_MS) {
        lastTypingSentRef.current = now;
        socketSend({ type: "typing", ref: activeRef });
      }
    },
    [isChat, activeRef, socketSend]
  );

  // -- sending --------------------------------------------------------

  const afterSend = useCallback(() => {
    setText("");
    setPendingFiles([]);
    setReplyTo(null);
    fetchMessages(activeRef, true);
    fetchConversations(true);
  }, [activeRef, fetchMessages, fetchConversations]);

  const handleWindowClosed = useCallback(() => {
    notification.warning({
      message: "Outside the 24-hour window",
      description:
        "WhatsApp only allows a free reply within 24 hours of the customer's "
        + "last message. Send an approved template to reopen the conversation.",
      btn: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.destroy();
            setTemplateOpen(true);
          }}
        >
          Send template
        </Button>
      ),
      duration: 8,
    });
  }, []);

  const send = async () => {
    if (!conversation) return;
    if (!text.trim() && !pendingFiles.length) return;

    setSending(true);
    try {
      if (isEmail) {
        const form = new FormData();
        form.append("text", text);
        if (subject) form.append("subject", subject);
        pendingFiles.forEach((file) => form.append("files", file));
        await api.sendWithFiles(conversation.id, form);
      } else if (isChat) {
        let response;
        if (pendingFiles.length) {
          const form = new FormData();
          form.append("text", text);
          form.append("file", pendingFiles[0]);
          if (replyTo) form.append("reply_to", replyTo.id);
          response = await api.sendWithFiles(conversation.id, form);
        } else {
          response = await api.sendMessage(
            conversation.id,
            replyTo ? { text, reply_to: replyTo.id } : { text }
          );
        }
        const sent = response?.data;
        if (sent && sent.id) {
          const row = normalizeChatMessage(sent.message || sent);
          setMessages((previous) =>
            previous.some((item) => item.id === row.id) ? previous : [...previous, row]
          );
        }
      } else {
        await api.sendMessage(conversation.id, { type: "text", text });
      }
      afterSend();
    } catch (error) {
      if (error?.response?.status === 403
        && error.response.data?.error === "window_closed") {
        handleWindowClosed();
      } else {
        antdMessage.error(
          error?.response?.data?.error || "Could not send that message"
        );
      }
    }
    setSending(false);
  };

  // WhatsApp sends each file as its own message, the way the protocol works.
  const sendWhatsAppFile = async (file, type) => {
    if (!conversation) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("type", type);
      form.append("file", file);
      if (text.trim()) form.append("caption", text.trim());
      await api.sendWithFiles(conversation.id, form);
      afterSend();
    } catch (error) {
      if (error?.response?.status === 403
        && error.response.data?.error === "window_closed") {
        handleWindowClosed();
      } else {
        antdMessage.error("Could not send that file");
      }
    }
    setSending(false);
  };

  // -- thread rows ----------------------------------------------------

  const threadRows = useMemo(() => {
    const dated = withDateSeparators(messages, "sent_at");
    return isEmail ? withSubjectDividers(dated) : dated;
  }, [messages, isEmail]);

  const windowClosed =
    isWhatsApp && conversation?.whatsapp && conversation.whatsapp.window_open === false;

  // -- render ---------------------------------------------------------

  return (
    <div style={{ padding: 16 }}>
      <Space
        wrap
        style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}
      >
        <Space wrap>
          <Segmented
            value={channel}
            onChange={(value) => updateParams({ channel: value, source: null, c: null })}
            options={channelOptions(unread)}
          />

          <Select
            allowClear
            placeholder="All products"
            style={{ minWidth: 190 }}
            value={productId}
            onChange={(value) => updateParams({ product: value, source: null, c: null })}
            options={products.map((product) => ({
              value: product.id,
              label: product.name,
            }))}
          />

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 260 }}
            placeholder={meta.sourcePlaceholder}
            value={sourceId}
            onChange={(value) => updateParams({ source: value, c: null })}
            options={sourceOptions}
          />

          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search conversations"
            style={{ width: 220 }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Space>

        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchConversations();
            if (activeRef) fetchMessages(activeRef);
          }}
        >
          Refresh
        </Button>
      </Space>

      <div
        style={{
          display: "flex",
          height: "calc(100vh - 190px)",
          minHeight: 460,
          border: "1px solid #e8e8e8",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {/* left: conversations */}
        <div
          style={{
            width: 340,
            flexShrink: 0,
            borderRight: "1px solid #e8e8e8",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div
            style={{
              background: theme.header,
              color: "#fff",
              padding: "10px 14px",
              fontWeight: 500,
            }}
          >
            {meta.label} · {conversations.length}
            {isChat ? (
              <Tooltip
                title={
                  socketConnected
                    ? "Live — new chats arrive instantly"
                    : `Reconnecting — checking every ${CHAT_POLL_MS / 1000}s`
                }
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    marginLeft: 8,
                    background: socketConnected ? "#52c41a" : "#faad14",
                  }}
                />
              </Tooltip>
            ) : null}
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <ConversationList
              conversations={conversations}
              loading={listLoading}
              selectedId={activeRef}
              onSelect={openConversation}
              accentColor={theme.accent}
              showSource={!sourceId}
              emptyText={meta.emptyText}
            />
          </div>
        </div>

        {/* right: thread */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {!conversation ? (
            <div
              style={{
                flex: 1,
                display: "grid",
                placeItems: "center",
                background: theme.background,
              }}
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Pick a conversation to start reading"
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: "8px 14px",
                  borderBottom: "1px solid #e8e8e8",
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Title level={5} style={{ margin: 0 }}>
                      {isChat ? chatTitle(conversation) : conversation.counterparty.display_name}
                    </Title>
                    {isChat ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {chatSubtitle(conversation)}
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {conversation.counterparty.address}
                        {conversation.source?.address
                          ? ` · via ${conversation.source.address}`
                          : ""}
                      </Text>
                    )}
                  </div>
                  {isWhatsApp && (
                    <Button size="small" onClick={() => setTemplateOpen(true)}>
                      Send template
                    </Button>
                  )}
                  {isChat && (
                    <ChatSessionActions
                      conversation={conversation}
                      messages={messages}
                      onChanged={(patch) => patchConversation(conversation.id, patch)}
                    />
                  )}
                </div>
                <div style={{ marginTop: 6 }}>
                  <IdentityBadges identity={identity} loading={identityLoading} />
                  {isChat && !identityLoading ? (
                    <Button
                      size="small"
                      type="link"
                      icon={<LinkOutlined />}
                      onClick={() => setLinkAccountOpen(true)}
                      style={{ paddingInline: 4 }}
                    >
                      {identity?.linked_account_id ? "Change account" : "Link account"}
                    </Button>
                  ) : null}
                  {identity?.matched_on?.length ? (
                    <Tooltip
                      title={`Matched on ${identity.matched_on.join(" and ")}: ${identity.matched_value}`}
                    >
                      <InfoCircleOutlined
                        style={{ color: "#8696a0", marginLeft: 6, fontSize: 12 }}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  background: theme.background,
                  padding: "10px 24px",
                }}
              >
                {threadLoading && !messages.length ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    <Spin />
                  </div>
                ) : (
                  threadRows.map((row) => {
                    if (row._type === "date-separator")
                      return <Divider key={row.key} label={row.label} />;
                    if (row._type === "subject-divider")
                      return (
                        <Divider
                          key={row.key}
                          label={row.label}
                          icon={<MdMailOutline />}
                        />
                      );
                    if (isChat)
                      return (
                        <ChatMessage
                          key={row.id}
                          message={row}
                          palette={theme}
                          onReply={(target) => setReplyTo(target)}
                        />
                      );
                    return (
                      <EnvelopeMessage
                        key={row.id}
                        message={row}
                        palette={theme}
                      />
                    );
                  })
                )}
                {isChat && visitorTyping ? (
                  <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                    Visitor is typing…
                  </Text>
                ) : null}
                <div ref={endRef} />
              </div>

              {windowClosed ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ borderRadius: 0 }}
                  message="Outside WhatsApp's 24-hour window"
                  description="Send an approved template to reopen this conversation."
                  action={
                    <Button size="small" type="primary" onClick={() => setTemplateOpen(true)}>
                      Send template
                    </Button>
                  }
                />
              ) : (
                <ChatComposer
                  value={text}
                  onChange={isChat ? onComposerChange : setText}
                  onSend={send}
                  sending={sending}
                  accentColor={theme.accent}
                  enterSends={!isEmail}
                  placeholder={isEmail ? "Write a reply" : "Type a message"}
                  onAttach={
                    isChat
                      ? (file) => {
                          if (file.size > CHAT_MAX_FILE_BYTES) {
                            antdMessage.error("Files are limited to 10 MB");
                            return;
                          }
                          setPendingFiles([file]);
                        }
                      : undefined
                  }
                  attachAccept={isChat ? CHAT_ATTACH_ACCEPT : undefined}
                  quickMessages={isChat ? quickMessages : undefined}
                  header={
                    isChat && (replyTo || pendingFiles.length) ? (
                      <div style={{ marginBottom: 6 }}>
                        {replyTo ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background: "#fff",
                              borderLeft: `3px solid ${theme.accent || "#8696a0"}`,
                              borderRadius: 6,
                              padding: "6px 6px 6px 10px",
                              marginBottom: pendingFiles.length ? 6 : 0,
                              fontSize: 13,
                            }}
                          >
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#54656f",
                              }}
                            >
                              <strong style={{ color: "#111b21" }}>Replying to: </strong>
                              {replyPreview(replyTo)}
                            </span>
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              aria-label="Cancel reply"
                              onClick={() => setReplyTo(null)}
                            />
                          </div>
                        ) : null}
                        {pendingFiles.length ? (
                          <Tag closable icon={<PaperClipOutlined />} onClose={() => setPendingFiles([])}>
                            {pendingFiles[0].name}
                          </Tag>
                        ) : null}
                      </div>
                    ) : isEmail ? (
                      <div style={{ marginBottom: 6 }}>
                        <Input
                          size="small"
                          addonBefore="Subject"
                          value={subject}
                          placeholder={
                            conversation.email?.subject
                              ? `Re: ${conversation.email.subject}`
                              : "Subject"
                          }
                          onChange={(event) => setSubject(event.target.value)}
                        />
                        {pendingFiles.length ? (
                          <Space wrap style={{ marginTop: 6 }}>
                            {pendingFiles.map((file, index) => (
                              <Tag
                                key={`${file.name}-${index}`}
                                closable
                                onClose={() =>
                                  setPendingFiles((previous) =>
                                    previous.filter((_, i) => i !== index)
                                  )
                                }
                              >
                                {file.name}
                              </Tag>
                            ))}
                          </Space>
                        ) : null}
                      </div>
                    ) : null
                  }
                  attachmentContent={
                    !isWhatsApp ? null : (
                      <WhatsAppAttachmentMenu
                        onPickFile={sendWhatsAppFile}
                        onLocation={() =>
                          antdMessage.info("Send a location from the WhatsApp page")
                        }
                        onContact={() =>
                          antdMessage.info("Send a contact card from the WhatsApp page")
                        }
                      />
                    )
                  }
                />
              )}

              {/* Email stages attachments and sends them with the body as one
                  mail, so the picker sits outside the popover menu. */}
              {isEmail && !windowClosed ? (
                <div style={{ background: "#f0f2f5", padding: "0 12px 8px" }}>
                  <Upload
                    multiple
                    showUploadList={false}
                    beforeUpload={(file) => {
                      setPendingFiles((previous) => [...previous, file]);
                      return false;
                    }}
                  >
                    <Button size="small" type="text" icon={<PaperClipOutlined />}>
                      Attach files
                    </Button>
                  </Upload>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {isChat && conversation ? (
        <LinkAccountModal
          open={linkAccountOpen}
          conversationRef={conversation.id}
          linkedAccountId={identity?.linked_account_id}
          onClose={() => setLinkAccountOpen(false)}
          onLinked={() => {
            fetchIdentity(conversation.id);
            fetchMessages(conversation.id, true);
          }}
        />
      ) : null}

      <SendTemplateModal
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        businessNumberId={conversation?.source?.id}
        contactWaId={conversation?.counterparty?.address}
        onSuccess={() => {
          setTemplateOpen(false);
          afterSend();
        }}
      />
    </div>
  );
};

export default CommunicationBox;
